import { LatLngTuple } from 'leaflet'
import React, { useEffect, useMemo, useRef } from 'react'
import { useMap } from 'react-leaflet'
import { ILocality, IMooring, IPosition, IPositionType } from '../../../../generated-types'
import { IProjectInfo } from 'src/generated-types/project'

interface IMapBoundsComponent {
  localities?: ILocality[]
  moorings?: IMooring[]
  positions?: IPosition[]
  additionalPositions?: LatLngTuple[]
  zoom?: number
  projects?: IProjectInfo[]
  fitAllPositions?: boolean
}

const MapBoundsComponent: React.FC<IMapBoundsComponent> = ({ localities, projects, moorings, positions, additionalPositions, zoom, fitAllPositions }) => {
  const map = useMap()
  const boundaries: LatLngTuple[] = useMemo(() => {
    const positionArray: LatLngTuple[] = []
    if (localities) {
      localities.forEach(locality => {
        locality.moorings?.forEach(mooring => {
          if (mooring?.positions?.length > 0) {
            positionArray.push([mooring?.positions[0]?.latitude, mooring?.positions[0]?.longitude])
          }
        })
      })
    } else if (moorings) {
      moorings?.forEach(mooring => {
        if (mooring?.positions && mooring.positions.length > 0) {
          collectBoundsPositions(mooring.positions, positionArray)
        }
      })
    } else if (projects) {
      const includePosition = fitAllPositions ? hasValidCoords : validatePosition
      projects?.forEach(project => {
        if (project?.positions && project?.positions?.length > 0) {
          project?.positions?.forEach(position => {
            if (includePosition(position)) {
              positionArray.push([position?.latitude, position?.longitude])
            }
          })
        }
      })
    } else if (positions) {
      collectBoundsPositions(positions, positionArray)
    }
    additionalPositions?.forEach(position => {
      if (Number.isFinite(position[0]) && Number.isFinite(position[1]) && !(position[0] === 0 && position[1] === 0)) {
        positionArray.push(position)
      }
    })
    return positionArray
  }, [localities, additionalPositions, moorings, positions, projects, fitAllPositions])

  const fittedBoundsKey = useRef<string>()
  useEffect(() => {
    const boundsKey = boundaries.map(b => b.join(',')).join(';')
    if (boundsKey === fittedBoundsKey.current) return
    fittedBoundsKey.current = boundsKey
    if (boundaries.length > 0) {
      map.fitBounds(boundaries, {
        animate: false,
        padding: [25, 25],
        duration: 100,
        maxZoom: localities ? 10 : undefined,
      })
    }
  }, [boundaries])

  useEffect(() => {
    if (zoom && zoom != 16) {
      map.setZoom(zoom)
    }
  }, [zoom])

  return null
}

const BOUNDS_POSITION_TYPES: ReadonlySet<IPositionType> = new Set([
  IPositionType.Corner,
  IPositionType.Cage,
  IPositionType.FrameLines,
])

export function validatePosition(position: IPosition): boolean {
  return BOUNDS_POSITION_TYPES.has(position.type) && position?.latitude !== 0 && position?.longitude !== 0
}

function hasValidCoords(position: IPosition): boolean {
  return position?.latitude !== 0 && position?.longitude !== 0
}

function collectBoundsPositions(positions: IPosition[], target: LatLngTuple[]): void {
  const matched = positions.filter(validatePosition)
  const source = matched.length > 0 ? matched : positions.filter(hasValidCoords)
  source.forEach(p => target.push([p.latitude, p.longitude]))
}

export default MapBoundsComponent
